import { useEffect, useMemo, useState } from "react";
import type { Coordinate } from "../../entities/tracking";
import { isOffRoute, metersBetween } from "./polyline";
import { useRoutePolyline } from "./useRoutePolyline";

// Itinéraire routier DEPUIS la Main, recalculé seulement quand il le faut.
//
// ## Pourquoi ce hook existe
// La mécanique vivait à l'intérieur du suivi des COURSES. Le suivi des
// COMMANDES et celui des URGENCES, eux, n'avaient rien : ils traçaient une
// ligne droite de la Main au client. Le même défaut, corrigé à un seul endroit,
// restait donc entier sur les deux autres — et une urgence est précisément le
// moment où un trait qui traverse les pâtés de maisons se remarque le plus.
//
// La règle est partout la même, elle n'a pas à être réécrite trois fois.
//
// ## Le compromis qu'il arbitre
// Un itinéraire est facturé PAR CALCUL, pas par position GPS. En redemander un
// à chaque ping coûterait un appel par seconde et par client ; n'en demander
// aucun laisse le trait de secours à l'écran.
//
// L'origine n'est donc PAS la position courante, mais la dernière position
// depuis laquelle une route a été demandée. On ne redemande que lorsque la Main
// QUITTE réellement le tracé — mauvaise rue, détour, ou début de trajet. Sur un
// parcours nominal cela fait un seul appel ; sur un parcours sinueux,
// quelques-uns. Jamais un par seconde.

/**
 * Déplacement minimal, depuis le dernier calcul, avant d'en autoriser un autre.
 *
 * 40 m : au-dessus du bruit d'un GPS de téléphone en ville, bien en dessous de
 * ce qui distingue deux rues.
 */
const MIN_REROUTE_MOVE_M = 40;

/** Clé stable (~1 m) : deux positions à quelques centimètres sont la même. */
function coordsKey(coords: Coordinate[]): string {
  return coords.map((c) => `${c.latitude.toFixed(5)},${c.longitude.toFixed(5)}`).join("|");
}

export type LiveRoute = {
  /**
   * Le tracé à dessiner. Routier dès qu'il est disponible ; sinon la ligne
   * droite de secours, qui décrit une DIRECTION et non un trajet.
   */
  routePoints: Coordinate[];
  /** Le tracé rendu suit-il les rues ? (`false` = repli ligne droite) */
  hasRoadRoute: boolean;
  /** Un itinéraire est-il en cours de calcul ? */
  isRouting: boolean;
};

/**
 * Itinéraire routier de `origin` vers `destinations`, dans l'ordre.
 *
 * `origin` est la position live de la Main ; `destinations` les points restant
 * à atteindre — les arrêts déjà traités ne doivent PAS y figurer, sinon la
 * ligne revient sur ses pas.
 *
 * `enabled` à `false` (course terminée, annulée) coupe tout : ni calcul, ni
 * tracé conservé.
 */
export function useLiveRoute(
  origin: Coordinate | null | undefined,
  destinations: Coordinate[],
  enabled = true,
): LiveRoute {
  // Les écrans reconstruisent ces tableaux à chaque rendu. Sans clé stable,
  // chaque frappe au clavier dans un champ voisin relancerait un calcul.
  const destKey = coordsKey(destinations);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dests = useMemo(() => destinations, [destKey]);

  const originLat = origin?.latitude;
  const originLng = origin?.longitude;
  const liveOrigin = useMemo(
    () => (originLat != null && originLng != null ? { latitude: originLat, longitude: originLng } : null),
    [originLat, originLng],
  );

  // Position depuis laquelle la route affichée a été calculée.
  const [routeOrigin, setRouteOrigin] = useState<Coordinate | null>(null);

  // Changer de destination change de segment : l'origine précédente n'a plus
  // cours. Idem quand le suivi s'arrête.
  useEffect(() => {
    setRouteOrigin(null);
  }, [destKey, enabled]);

  const waypoints = useMemo(() => {
    if (!enabled || !routeOrigin || dests.length === 0) return [];
    return [routeOrigin, ...dests];
  }, [enabled, routeOrigin, dests]);

  const routeQuery = useRoutePolyline(waypoints);
  const roadRoute = enabled && routeQuery.data && routeQuery.data.length > 1 ? routeQuery.data : null;

  useEffect(() => {
    if (!enabled || !liveOrigin || dests.length === 0) return;

    // Premier tracé de ce segment.
    if (!routeOrigin) {
      setRouteOrigin(liveOrigin);
      return;
    }

    // ⚠️ VERROU INDISPENSABLE : ne rien redemander tant qu'un calcul est en vol.
    //
    // Le tracé PRÉCÉDENT reste affiché pendant le recalcul (`keepPreviousData`,
    // sans quoi la ligne droite réapparaîtrait à chaque fois). Mais la Main, elle,
    // en est justement écartée — c'est ce qui a déclenché le recalcul. Sans ce
    // verrou, chaque point GPS reçu pendant l'attente relancerait une requête :
    // on paierait un itinéraire par seconde, exactement ce que cette mécanique
    // cherche à éviter.
    if (routeQuery.isFetching) return;

    // ⚠️ SECOND VERROU : la Main doit avoir BOUGÉ depuis le dernier calcul.
    //
    // Sans lui, une seule situation suffit à faire boucler la mécanique : la
    // Main immobile hors d'atteinte d'une route — dans une cour, un marché, un
    // parking. Google fait alors partir l'itinéraire de la voie la plus proche,
    // à cent mètres de là ; `isOffRoute` reste donc vrai en permanence, et
    // chaque ping GPS redemande un itinéraire que le suivant redemandera
    // encore. Le verrou `isFetching` n'y change rien : il sérialise les appels,
    // il ne les arrête pas.
    //
    // Une Main qui n'a pas avancé n'a rien à recalculer. C'est aussi vrai
    // quand tout va bien : cela supprime les recalculs à l'arrêt, au feu rouge
    // comme pendant les achats.
    if (metersBetween(liveOrigin, routeOrigin) < MIN_REROUTE_MOVE_M) return;

    // Puis seulement si la Main a quitté la route qu'on lui dessine.
    if (roadRoute && isOffRoute(liveOrigin, roadRoute)) {
      setRouteOrigin(liveOrigin);
    }
  }, [enabled, liveOrigin, dests, routeOrigin, roadRoute, routeQuery.isFetching]);

  const routePoints = useMemo(() => {
    if (!enabled) return [];
    if (roadRoute) return roadRoute;
    // Ligne droite : DERNIER recours — clé Maps absente, Routes API en panne,
    // ou tout premier rendu avant que le tracé n'arrive. Elle ne doit jamais
    // rester longtemps à l'écran.
    if (liveOrigin) return [liveOrigin, ...dests];
    return dests.length >= 2 ? dests : [];
  }, [enabled, roadRoute, liveOrigin, dests]);

  return {
    routePoints,
    hasRoadRoute: Boolean(roadRoute),
    isRouting: routeQuery.isFetching,
  };
}
